<script setup lang="ts">
// types
import type { SectionHeroGradeProps } from '@ts-sections/section-hero-grade/interfaces'
// hooks
import { useSectionHeroGrade } from '@hooks/sections/section-hero-grade.hook'
// assets
import gradientSkyGreenDesktop from '@images/hero-grade/gradient-sky-green-desktop.svg?raw'
import gradientSkyGreenMobile from '@images/hero-grade/gradient-sky-green-mobile.svg?raw'
import gradientSunshinePinkDesktop from '@images/hero-grade/gradient-sunshine-pink-desktop.svg?raw'
import gradientSunshinePinkMobile from '@images/hero-grade/gradient-sunshine-pink-mobile.svg?raw'
import gradientUltravioletPinkDesktop from '@images/hero-grade/gradient-ultraviolet-pink-desktop.svg?raw'
import gradientUltravioletPinkMobile from '@images/hero-grade/gradient-ultraviolet-pink-mobile.svg?raw'
// components
import BlockGradeSelector from '@Blocks/BlockGradeSelector.vue'
import BlockHomepageTitle from '@Blocks/BlockHomepageTitle.vue'
import BlockStatsItem from '@Blocks/BlockStatsItem.vue'
import TrustpilotWidget from '@Widgets/Trustpilot/TrustpilotWidget.vue'

defineOptions({
  name: 'SectionHeroGrade',
})

const props = defineProps<{
  section: SectionHeroGradeProps
}>()

const GRADIENTS = {
  ultravioletPink: { desktop: gradientUltravioletPinkDesktop, mobile: gradientUltravioletPinkMobile },
  skyGreen: { desktop: gradientSkyGreenDesktop, mobile: gradientSkyGreenMobile },
  sunshinePink: { desktop: gradientSunshinePinkDesktop, mobile: gradientSunshinePinkMobile },
} as const

const {
  refTitle,
  refSubtitle,
  refGradeSelector,
  refStats,
  refShowStats,
  refGradientColor,
} = useSectionHeroGrade({
  section: () => props.section,
})
</script>

<template>
  <section
    class="section-hero-grade"
    :data-component="section.__component"
  >
    <div class="section-hero-grade__surface">
      <div
        class="section-hero-grade__gradient"
        aria-hidden="true"
      >
        <div
          class="section-hero-grade__gradient-mobile"
          v-html="GRADIENTS[refGradientColor].mobile"
        />
        <div
          class="section-hero-grade__gradient-desktop"
          v-html="GRADIENTS[refGradientColor].desktop"
        />
      </div>

      <div class="section-hero-grade__content">
        <div class="section-hero-grade__title-block">
          <TrustpilotWidget class="section-hero-grade__trustpilot" />
          <BlockHomepageTitle
            v-if="refTitle"
            :block="refTitle"
            desktop-size="display-l"
          />
          <p
            v-if="refSubtitle"
            class="section-hero-grade__subtitle btly-text-body-s md:btly-text-body-l"
          >
            {{ refSubtitle }}
          </p>
        </div>

        <BlockGradeSelector
          v-if="refGradeSelector"
          :block="refGradeSelector"
        />
      </div>
    </div>

    <div
      v-if="refShowStats"
      class="section-hero-grade__stats"
    >
      <ul class="section-hero-grade__stats-list">
        <li
          v-for="(stat, index) in refStats"
          :key="stat.id ?? index"
          class="section-hero-grade__stats-item"
        >
          <BlockStatsItem :block="stat" />
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped lang="scss" src="@scss/components/Sections/SectionHeroGrade.scss" />
